import { apiURL } from './constants';
import axios from 'axios';

const unitBaseURL = `${apiURL}/api/shipment_unit`;
const unitEventBaseURL = `${apiURL}/api/shipment_unit_tracking_event`;

function getAuthHeaders() {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
}

function appendParams(searchParams, params) {
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
}

/**
 * Global axios interceptor resolves failed requests with `error.response` instead of rejecting.
 * Call this on every response so 4xx/5xx surface as thrown errors to callers.
 */
function throwIfErrorResponse(response) {
  if (response == null) {
    const err = new Error('No response from server');
    throw err;
  }
  if (typeof response.status === 'number' && response.status >= 400) {
    const err = new Error(`HTTP ${response.status}`);
    err.response = response;
    throw err;
  }
  return response;
}

// --- Shipment units ---

/**
 * List / create shipment units.
 * Permissions: shipment_units_list | shipment_units_create
 * @param {Object} params - search, shipping_company, origin_warehouse, destination_warehouse, location,
 *   deleted, page, page_size, pagination
 * List results nest `shipping_company`, `origin_warehouse`, and `destination_warehouse` as objects
 * (`{ id, name, name_ar, code, active }`); writes still take integer ids.
 */
export async function listShipmentUnits(params = {}) {
  getAuthHeaders();
  const searchParams = new URLSearchParams();
  appendParams(searchParams, params);
  const qs = searchParams.toString();
  const response = await axios.get(`${unitBaseURL}/${qs ? `?${qs}` : ''}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('listShipmentUnits error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

export async function getShipmentUnit(id) {
  getAuthHeaders();
  const response = await axios.get(`${unitBaseURL}/${id}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('getShipmentUnit error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * @param {Object} body - serial_number, unit_number (optional, max 128), status (required on create), shipments (ids), shipped_at,
 *   shipping_company, origin_warehouse, destination_warehouse, location (integer id on write),
 *   tracking_url, expected_destination_eta, expected_warehouse_eta, ship_name, note, event_type (slug, optional on create)
 *   On GET responses: `shipping_company`, `origin_warehouse`, and `destination_warehouse` are nested
 *   objects `{ id, name, name_ar, code, active }`; `location` is the display name (string) and
 *   `location_id` is the id (read-only); `shipments` is an array of shipment ids.
 */
export async function createShipmentUnit(body) {
  getAuthHeaders();
  const response = await axios.post(`${unitBaseURL}/`, body, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('createShipmentUnit error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

export async function updateShipmentUnit(id, body, partial = true) {
  getAuthHeaders();
  const method = partial ? 'patch' : 'put';
  const response = await axios[method](`${unitBaseURL}/${id}/`, body, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('updateShipmentUnit error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/** Soft-delete (sets deleted=true). Permission: shipment_units_delete */
export async function deleteShipmentUnit(id) {
  getAuthHeaders();
  const response = await axios.delete(`${unitBaseURL}/${id}/`, {
    withCredentials: true,
  }).catch((error) => {
    console.error('deleteShipmentUnit error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Append shipments to a unit without replacing full membership.
 * Permission: shipment_units_assign_shipments
 * @param {number} unitId - shipment unit primary key
 * @param {number[]} shipmentIds - shipment primary keys
 */
export async function assignShipmentsToShipmentUnit(unitId, shipmentIds) {
  getAuthHeaders();
  const response = await axios.post(
    `${unitBaseURL}/${unitId}/assign-shipments/`,
    { shipments: Array.isArray(shipmentIds) ? shipmentIds : [] },
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    },
  ).catch((error) => {
    console.error('assignShipmentsToShipmentUnit error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Append shipments to a unit, or unassign when shipment_unit is null/omitted.
 * Permission: shipment_units_assign_shipments
 * @param {{ shipments: number[], shipment_unit?: number | null }} body
 */
export async function bulkAssignShipmentsToShipmentUnit(body) {
  getAuthHeaders();
  const response = await axios.post(`${unitBaseURL}/bulk-assign-shipments/`, body, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('bulkAssignShipmentsToShipmentUnit error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Recalculate prices for all non-deleted shipments on a unit (group-aware).
 * POST /api/shipment_unit/{id}/recalculate-prices/
 * Permission: shipments_recalculate_price
 * @param {number} unitId - shipment unit primary key
 */
export async function recalculateShipmentUnitPrices(unitId) {
  getAuthHeaders();
  const response = await axios.post(
    `${unitBaseURL}/${unitId}/recalculate-prices/`,
    {},
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    },
  ).catch((error) => {
    console.error('recalculateShipmentUnitPrices error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Bulk-set pricing_tariff_code on all non-deleted shipments in a unit.
 * PUT /api/shipment_unit/{id}/bulk-update-tariff-code/
 * Permission: shipments_update
 * @param {number} unitId - shipment unit primary key
 * @param {{ pricing_tariff_code?: string | null, recalculate_prices?: boolean }} body
 */
export async function bulkUpdateShipmentUnitTariffCode(unitId, body = {}) {
  getAuthHeaders();
  const response = await axios.put(
    `${unitBaseURL}/${unitId}/bulk-update-tariff-code/`,
    body,
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    },
  ).catch((error) => {
    console.error('bulkUpdateShipmentUnitTariffCode error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * List pricing rules scoped to a shipment unit.
 * GET /api/shipment_unit/{id}/pricing-rules/
 * Permission: pricing_rules_list
 */
export async function listShipmentUnitPricingRules(unitId) {
  getAuthHeaders();
  const response = await axios.get(`${unitBaseURL}/${unitId}/pricing-rules/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('listShipmentUnitPricingRules error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Create a pricing rule scoped to a shipment unit (backend forces shipment_unit from URL).
 * POST /api/shipment_unit/{id}/pricing-rules/
 * Permission: pricing_rules_create
 */
export async function createShipmentUnitPricingRule(unitId, body) {
  getAuthHeaders();
  const response = await axios.post(`${unitBaseURL}/${unitId}/pricing-rules/`, body, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('createShipmentUnitPricingRule error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

// --- Shipment unit tracking events ---

/**
 * @param {Object} params - event_type (code), search, shipment_unit, page, page_size, deleted, ...
 */
export async function listShipmentUnitTrackingEvents(params = {}) {
  getAuthHeaders();
  const searchParams = new URLSearchParams();
  appendParams(searchParams, params);
  const qs = searchParams.toString();
  const response = await axios.get(`${unitEventBaseURL}/${qs ? `?${qs}` : ''}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('listShipmentUnitTrackingEvents error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

export async function getShipmentUnitTrackingEvent(id) {
  getAuthHeaders();
  const response = await axios.get(`${unitEventBaseURL}/${id}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('getShipmentUnitTrackingEvent error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * @param {Object} body - shipment_unit, event_type (code), status_snapshot, location, timestamp, description
 */
export async function createShipmentUnitTrackingEvent(body) {
  getAuthHeaders();
  const payload = { ...body };
  if (payload.location && typeof payload.location === 'object' && payload.location.id != null) {
    payload.location = payload.location.id;
  }
  const response = await axios.post(`${unitEventBaseURL}/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('createShipmentUnitTrackingEvent error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Update (full or partial) a shipment unit tracking event.
 * Permission: shipment_unit_tracking_events_update
 *
 * Include `timestamp` (ISO 8601 UTC) only when changing it — presence triggers latest-event
 * re-sync on the parent unit and child shipments; omit it to sync from the edited event.
 */
export async function updateShipmentUnitTrackingEvent(id, body, partial = true) {
  getAuthHeaders();
  const payload = { ...body };
  if (payload.location && typeof payload.location === 'object' && payload.location.id != null) {
    payload.location = payload.location.id;
  }
  const method = partial ? 'patch' : 'put';
  const response = await axios[method](`${unitEventBaseURL}/${id}/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('updateShipmentUnitTrackingEvent error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

export async function deleteShipmentUnitTrackingEvent(id) {
  getAuthHeaders();
  const response = await axios.delete(`${unitEventBaseURL}/${id}/`, {
    withCredentials: true,
  }).catch((error) => {
    console.error('deleteShipmentUnitTrackingEvent error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Bulk update shipment unit tracking events.
 * Permission: shipment_unit_tracking_events_bulk_update
 * Body: array of { id, ...fields }. Each item is a partial patch.
 *
 * Writable per item: `timestamp`, `event_type`, `status_snapshot`, `location`, `description`,
 * `shipment_unit`. When `timestamp` is included, parent unit and child shipments are reconciled
 * from the latest remaining unit event by timestamp.
 */
export async function bulkUpdateShipmentUnitTrackingEvents(items) {
  getAuthHeaders();
  const payload = items.map((item) => {
    const { id, ...rest } = item;
    const out = { id, ...rest };
    if (out.location && typeof out.location === 'object' && out.location.id != null) out.location = out.location.id;
    if (out.shipment_unit && typeof out.shipment_unit === 'object' && out.shipment_unit.id != null) {
      out.shipment_unit = out.shipment_unit.id;
    }
    return out;
  });
  const response = await axios.put(`${unitEventBaseURL}/bulk-update/`, payload, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('bulkUpdateShipmentUnitTrackingEvents error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Bulk soft-delete shipment unit tracking events.
 * Permission: shipment_unit_tracking_events_bulk_delete
 * Body: array of IDs
 */
export async function bulkDeleteShipmentUnitTrackingEvents(ids) {
  getAuthHeaders();
  const response = await axios.delete(`${unitEventBaseURL}/bulk-delete/`, {
    data: ids,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('bulkDeleteShipmentUnitTrackingEvents error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}
